import { Subject } from 'rxjs'
import CounterWidget from './counter.js'
import Poller from './poller.js'

/**
 * CSS selector string for elements configuring a Poller.
 * @type {string}
 */
const pollerSelector = '[data-socialproof-url]:not([data-socialproof-url=""])'

/**
 * CSS selector string for elements configuring a CounterWidget.
 * @type {string}
 */
const counterSelector = `${pollerSelector}[data-counter], ${pollerSelector} [data-counter]`

/**
 * CSS selector string for elements representing a CounterWidget (data-counter has a value).
 * @type {string}
 */
const counterWidgetSelector = '[data-counter]:not([data-counter=""])'

/**
 * A flag to enable more output on the console.
 * @type {boolean}
 */
const debug = !!parseInt(sessionStorage?.getItem('mo_debug'))

/**
 * Print to debug output to the console in debug mode.
 *
 * @param {...any} args - The arguments to print
 */
function printDebug (...args) {
  if (debug) {
    console.debug('[Socialproof]', ...args)
  }
}

/**
 * Iterate over decendents matching the selector, including the element itself.
 *
 * @param {HTMLElement} element - The element to query
 * @param {string} selector - The selector string
 *
 * @yields {HTMLElement} The next matching element
 */
function * querySelectorAllAndSelf (element, selector) {
  if (element.nodeType !== Node.ELEMENT_NODE) {
    return
  }
  if (element.matches(selector)) {
    yield element
  }
  yield * element.querySelectorAll(selector)
}

/**
 * Set up a poller based on the data attributes of an element.
 *
 * @param {HMTLElement} element - The socialproof element
 */
function initPoller (element) {
  const url = element.dataset.socialproofUrl
  const options = {}
  for (const key in element.dataset) {
    options[key] = parseFloat(element.dataset[key]) || element.dataset[key]
  }
  const poller = new Poller(url, options)
  printDebug('poller initialized', poller)
  return poller
}

/**
 * Set up a counter based on the data attributes of an element and its parent counters.
 *
 * @param {HMTLElement} element - The counter element
 */
function initCounter (element) {
  let options = {}
  let counter = element
  while ((counter = counter.parentElement.closest(counterSelector)) !== null) {
    // Only add options that haven’t been set yet and ignore any parents’s htmlAttr and styleProp.
    options = { ...counter.dataset, ...options, ...{ htmlAttr: null, styleProp: null } }
  }
  const widget = new CounterWidget(element, options)
  printDebug('counter initialized', widget)
  return widget
}

/**
 * Initialize polling and widgets on DOM elements.
 *
 * @param {document} document - The web page
 */
export default function init (document) {
  const subscriptions = new Map()
  const mutations$ = new Subject()
  const mutationObserver = new MutationObserver((mutationList) => {
    for (const record of mutationList) {
      mutations$.next(record)
    }
  })

  for (const pollerElem of document.querySelectorAll(pollerSelector)) {
    const poller = initPoller(pollerElem)
    for (const counterElem of querySelectorAllAndSelf(pollerElem, counterWidgetSelector)) {
      subscriptions.set(counterElem, initCounter(counterElem).poll(poller))
      printDebug('subscriptions updated', subscriptions)
    }
    // React to changes to the DOM.
    mutationObserver.observe(pollerElem, { subtree: true, childList: true })
    mutations$.subscribe((record) => {
      printDebug('DOM changed', record)
      for (const newElem of record.addedNodes) {
        for (const counterElem of querySelectorAllAndSelf(newElem, counterWidgetSelector)) {
          subscriptions.set(counterElem, initCounter(counterElem).poll(poller))
          printDebug('subscriptions updated', subscriptions)
        }
      }
      for (const delElem of record.removedNodes) {
        for (const counterElem of querySelectorAllAndSelf(delElem, counterWidgetSelector)) {
          if (subscriptions.get(counterElem)) {
            subscriptions.get(counterElem).unsubscribe()
            subscriptions.delete(counterElem)
            printDebug('subscriptions updated', subscriptions)
          }
        }
      }
    })
  }
}
